import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch } from 'src/redux/storeHooks'
import { getPlatformFieldsAsync } from 'src/redux/actions/productActions'
import { getFieldTreeSearchAsync } from 'src/redux/actions/templateAndPricingActions'

const SEARCH_DEBOUNCE_MS = 400
const PAGE_LIMIT = 30

/**
 * Hook for platform-specific category tree search.
 *
 * Uses API 1 (getPlatformFieldsAsync) to resolve the CATEGORY field ID on mount,
 * then API 2 (getFieldTreeSearchAsync) to search/paginate categories.
 *
 * All state is local — no Redux slice reads or writes are consumed.
 */
export function usePlatformCategoryTree(platformId: number | undefined) {
  const dispatch = useAppDispatch()

  // ── State ──
  const [fieldId, setFieldId] = useState<number | null>(null)
  const [results, setResults] = useState<any[]>([])
  const [count, setCount] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [fieldLoading, setFieldLoading] = useState(false)
  const [currentTerm, setCurrentTerm] = useState('')

  // Refs for debounce and stale-request guards
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const searchSeqRef = useRef(0) // monotonic counter to discard stale responses

  const hasMore = results.length < count

  // ── API 1: Resolve field ID on mount / platformId change ──
  useEffect(() => {
    // Reset everything when platformId changes
    setFieldId(null)
    setResults([])
    setCount(0)
    setPage(1)
    setLoading(false)
    setCurrentTerm('')
    searchSeqRef.current += 1

    if (platformId == null) {
      setFieldLoading(false)
      return
    }

    let cancelled = false
    setFieldLoading(true)

    dispatch(
      getPlatformFieldsAsync({
        platformId,
        otherParams: { field_code: 'CATEGORY' },
      })
    ).then((response: any) => {
      if (cancelled) return
      const id = response?.payload?.[0]?.id ?? null
      setFieldId(id)
      setFieldLoading(false)
    }).catch(() => {
      if (!cancelled) setFieldLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [platformId, dispatch])

  // ── API 2: Internal fetch helper ──
  const fetchTree = useCallback(
    async (
      term: string,
      pageNum: number,
      fId: number,
      pId: number
    ): Promise<{ items: any[]; total: number } | null> => {
      try {
        const res: any = await dispatch(
          getFieldTreeSearchAsync({
            categoryId: pId,
            platform_field_id: fId,
            search_string: term,
            page: pageNum,
            limit: PAGE_LIMIT,
            use_pagination: true,
          })
        )
        const items: any[] = res?.payload?.results || res?.payload || []
        const total: number = res?.payload?.count ?? items.length
        return { items, total }
      } catch {
        return null
      }
    },
    [dispatch]
  )

  // ── search(term) — debounced ──
  const search = useCallback(
    (term: string) => {
      // Clear any pending debounce
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }

      const trimmed = term.trim()
      setCurrentTerm(trimmed)

      // If fieldId or platformId not ready, skip
      if (fieldId == null || platformId == null) {
        setLoading(false)
        return
      }

      // Capture sequence number for stale-response guard
      const seq = ++searchSeqRef.current

      // Reset results immediately for fresh search
      setResults([])
      setCount(0)
      setPage(1)
      setLoading(true)

      debounceRef.current = setTimeout(async () => {
        const data = await fetchTree(trimmed, 1, fieldId, platformId)
        // Discard if a newer search was initiated
        if (seq !== searchSeqRef.current) return

        if (data) {
          setResults(data.items)
          setCount(data.total)
          setPage(1)
        }
        setLoading(false)
      }, SEARCH_DEBOUNCE_MS)
    },
    [fieldId, platformId, fetchTree]
  )

  // ── Auto-fetch initial data once fieldId resolves ──
  useEffect(() => {
    if (fieldId != null && platformId != null) {
      search('')
    }
  }, [fieldId, platformId, search])

  // ── loadMore() — next page, append ──
  const loadMore = useCallback(async () => {
    if (!hasMore || loading || fieldId == null || platformId == null) return

    const nextPage = page + 1
    setLoading(true)

    const data = await fetchTree(currentTerm, nextPage, fieldId, platformId)
    if (data) {
      setResults((prev) => {
        // Deduplicate by id
        const existingIds = new Set(prev.map((item) => item.id))
        const newItems = data.items.filter((item) => !existingIds.has(item.id))
        return [...prev, ...newItems]
      })
      setCount(data.total)
      setPage(nextPage)
    }
    setLoading(false)
  }, [hasMore, loading, fieldId, platformId, page, currentTerm, fetchTree])

  // ── hydrateById(id) — isolated name resolution ──
  const hydrateById = useCallback(
    async (id: number): Promise<string | null> => {
      if (fieldId == null || platformId == null) return null

      try {
        const res: any = await dispatch(
          getFieldTreeSearchAsync({
            categoryId: platformId,
            platform_field_id: fieldId,
            search_string: '',
            page: 1,
            limit: 1,
            use_pagination: true,
            selected_child_id: id,
          })
        )
        const items: any[] = res?.payload?.results || res?.payload || []
        return items[0]?.api_name ?? null
      } catch {
        return null
      }
    },
    [fieldId, platformId, dispatch]
  )

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  return {
    results,
    loading,
    fieldLoading,
    hasMore,
    fieldId,
    search,
    loadMore,
    hydrateById,
  }
}
